/**
 * Circuit ML Recommendation System
 *
 * Analyzes circuit workout history to generate smart recommendations:
 * - Round count adjustments based on fatigue patterns
 * - Pace targets based on historical performance
 * - Sprint/run time recommendations for cardio circuits
 * - Recovery-aware adjustments
 */

import { getCircuitHistory, timeToSeconds, secondsToTime, type CircuitHistory } from './circuitAnalytics';
import { getSessionHistory, type SessionLog, type ExerciseLog, type CircuitSetLog } from './sessionStorage';

export interface CircuitPerformanceMetrics {
  exerciseName: string;
  sessionCount: number;
  avgRoundsCompleted: number;
  avgPaceDegradation: number;      // % slower last round vs first round
  avgRPE: number;
  consistencyScore: number;        // 0-100, higher = more consistent pace
  avgRunTime?: number;             // Average sprint/run time in seconds
  runTimeImprovement?: number;     // % improvement over sessions
  lastSessionDate: string | null;
  trend: 'improving' | 'maintaining' | 'declining' | 'insufficient_data';
}

export interface CircuitAdjustment {
  type: 'increase_rounds' | 'decrease_rounds' | 'maintain' | 'reduce_rest' | 'increase_rest';
  reason: string;
  suggestedRounds?: number;
  suggestedRestSeconds?: number;
  confidence: 'high' | 'medium' | 'low';
}

export interface CircuitRecommendation {
  exerciseName: string;
  metrics: CircuitPerformanceMetrics;
  adjustment: CircuitAdjustment;
  targetPace: {
    roundTimeSeconds: number;
    displayTime: string;
  };
  sprintRecommendation?: {
    targetTimeSeconds: number;
    displayTime: string;
    message: string;
  };
}

/**
 * Calculate pace degradation percentage (how much slower last round vs first round)
 */
function calculatePaceDegradation(rounds: CircuitSetLog[]): number {
  const times = rounds
    .filter(r => r.time && r.time !== '')
    .map(r => timeToSeconds(r.time));

  if (times.length < 2) return 0;

  const firstRound = times[0];
  const lastRound = times[times.length - 1];

  if (firstRound === 0) return 0;

  // Positive = got slower, negative = got faster
  return ((lastRound - firstRound) / firstRound) * 100;
}

/**
 * Calculate consistency score (how uniform the pace is across rounds)
 * 100 = perfectly consistent, 0 = highly variable
 */
function calculateConsistencyScore(rounds: CircuitSetLog[]): number {
  const times = rounds
    .filter(r => r.time && r.time !== '')
    .map(r => timeToSeconds(r.time));

  if (times.length < 2) return 100;

  const avg = times.reduce((a, b) => a + b, 0) / times.length;
  if (avg === 0) return 100;

  // Calculate standard deviation as % of average
  const variance = times.reduce((sum, t) => sum + Math.pow(t - avg, 2), 0) / times.length;
  const stdDev = Math.sqrt(variance);
  const coefficientOfVariation = (stdDev / avg) * 100;

  // Convert to score: 0% variation = 100, 20%+ variation = 0
  return Math.max(0, Math.min(100, 100 - coefficientOfVariation * 5));
}

/**
 * Extract sprint/run times from circuit rounds
 */
function extractRunTimes(rounds: CircuitSetLog[]): number[] {
  return rounds
    .filter(r => r.runTime && r.runTime !== '')
    .map(r => timeToSeconds(r.runTime!));
}

/**
 * Analyze circuit performance across multiple sessions
 */
export async function analyzeCircuitPerformance(
  exerciseName: string,
  sessionLimit: number = 10
): Promise<CircuitPerformanceMetrics> {
  const history = await getCircuitHistory(exerciseName, sessionLimit);

  if (history.length === 0) {
    return {
      exerciseName,
      sessionCount: 0,
      avgRoundsCompleted: 0,
      avgPaceDegradation: 0,
      avgRPE: 0,
      consistencyScore: 100,
      lastSessionDate: null,
      trend: 'insufficient_data',
    };
  }

  // Calculate metrics across sessions
  const paceDegradations = history.map(h => calculatePaceDegradation(h.rounds));
  const consistencyScores = history.map(h => calculateConsistencyScore(h.rounds));
  const roundCounts = history.map(h => h.rounds.length);
  const avgRPEs = history.map(h => h.totalRPE);

  // Collect all run times across sessions
  const allRunTimes = history.flatMap(h => extractRunTimes(h.rounds));

  // Calculate averages
  const avgPaceDegradation = paceDegradations.reduce((a, b) => a + b, 0) / paceDegradations.length;
  const avgConsistency = consistencyScores.reduce((a, b) => a + b, 0) / consistencyScores.length;
  const avgRounds = roundCounts.reduce((a, b) => a + b, 0) / roundCounts.length;
  const avgRPE = avgRPEs.reduce((a, b) => a + b, 0) / avgRPEs.length;

  // Calculate run time metrics if available
  let avgRunTime: number | undefined;
  let runTimeImprovement: number | undefined;

  if (allRunTimes.length > 0) {
    avgRunTime = allRunTimes.reduce((a, b) => a + b, 0) / allRunTimes.length;

    // Compare first half of sessions to second half for improvement trend
    if (history.length >= 4) {
      const firstHalf = history.slice(Math.floor(history.length / 2));
      const secondHalf = history.slice(0, Math.floor(history.length / 2));

      const firstHalfRunTimes = firstHalf.flatMap(h => extractRunTimes(h.rounds));
      const secondHalfRunTimes = secondHalf.flatMap(h => extractRunTimes(h.rounds));

      if (firstHalfRunTimes.length > 0 && secondHalfRunTimes.length > 0) {
        const firstAvg = firstHalfRunTimes.reduce((a, b) => a + b, 0) / firstHalfRunTimes.length;
        const secondAvg = secondHalfRunTimes.reduce((a, b) => a + b, 0) / secondHalfRunTimes.length;

        // Negative = getting faster (improvement)
        runTimeImprovement = ((secondAvg - firstAvg) / firstAvg) * 100;
      }
    }
  }

  // Determine trend based on recent vs older performance
  let trend: 'improving' | 'maintaining' | 'declining' | 'insufficient_data' = 'insufficient_data';

  if (history.length >= 3) {
    const recentSessions = history.slice(0, 2);
    const olderSessions = history.slice(2, 5);

    if (olderSessions.length > 0) {
      const recentAvgTime = recentSessions.reduce((sum, h) => sum + h.avgTime, 0) / recentSessions.length;
      const olderAvgTime = olderSessions.reduce((sum, h) => sum + h.avgTime, 0) / olderSessions.length;

      const timeChange = ((recentAvgTime - olderAvgTime) / olderAvgTime) * 100;

      if (timeChange < -5) {
        trend = 'improving';  // Getting faster
      } else if (timeChange > 5) {
        trend = 'declining';  // Getting slower
      } else {
        trend = 'maintaining';
      }
    }
  }

  return {
    exerciseName,
    sessionCount: history.length,
    avgRoundsCompleted: avgRounds,
    avgPaceDegradation,
    avgRPE,
    consistencyScore: avgConsistency,
    avgRunTime,
    runTimeImprovement,
    lastSessionDate: history[0]?.date || null,
    trend,
  };
}

/**
 * Generate circuit adjustment recommendation based on performance metrics
 */
export function generateCircuitAdjustment(
  metrics: CircuitPerformanceMetrics,
  currentTargetRounds: number
): CircuitAdjustment {
  // Not enough data for confident recommendations
  if (metrics.sessionCount < 2) {
    return {
      type: 'maintain',
      reason: 'Need more sessions to analyze pattern',
      confidence: 'low',
    };
  }

  // High pace degradation (>25%) suggests too many rounds or not enough recovery
  if (metrics.avgPaceDegradation > 25) {
    // Also check RPE - if RPE is low despite degradation, might be pacing issue
    if (metrics.avgRPE < 7) {
      return {
        type: 'increase_rest',
        reason: 'Pace drops significantly but RPE is moderate - try longer rest between rounds',
        suggestedRestSeconds: 90,
        confidence: 'medium',
      };
    }

    return {
      type: 'decrease_rounds',
      reason: `Pace drops ${metrics.avgPaceDegradation.toFixed(0)}% by end - reduce rounds to maintain quality`,
      // `Math.max(3, ...)` on a DECREASE turns a 2-round circuit into 3 —
      // a 50% volume INCREASE announced as "reduce rounds to maintain
      // quality". The floor for a reduction is 1 round, not 3.
      suggestedRounds: Math.max(1, currentTargetRounds - 1),
      confidence: 'high',
    };
  }

  // Low pace degradation with high consistency and manageable RPE - ready for progression
  if (metrics.avgPaceDegradation < 10 && metrics.consistencyScore > 75 && metrics.avgRPE <= 7) {
    return {
      type: 'increase_rounds',
      reason: 'Consistent pace and manageable effort - ready for more volume',
      suggestedRounds: currentTargetRounds + 1,
      confidence: 'high',
    };
  }

  // Moderate degradation with good consistency - maintain current volume
  if (metrics.avgPaceDegradation <= 20 && metrics.consistencyScore > 60) {
    return {
      type: 'maintain',
      reason: 'Good balance of challenge and consistency',
      confidence: 'high',
    };
  }

  // High RPE but maintaining pace - at capacity, don't increase
  if (metrics.avgRPE >= 8.5 && metrics.avgPaceDegradation <= 15) {
    return {
      type: 'maintain',
      reason: 'Working at high intensity - maintain current volume',
      confidence: 'medium',
    };
  }

  // Inconsistent pacing suggests need for more practice at current level
  if (metrics.consistencyScore < 50) {
    return {
      type: 'maintain',
      reason: 'Focus on consistent pacing before adding volume',
      confidence: 'medium',
    };
  }

  // Default: maintain
  return {
    type: 'maintain',
    reason: 'Current volume appropriate for fitness level',
    confidence: 'medium',
  };
}

/**
 * Generate sprint/run time recommendation
 */
function generateSprintRecommendation(
  metrics: CircuitPerformanceMetrics
): CircuitRecommendation['sprintRecommendation'] | undefined {
  if (!metrics.avgRunTime) return undefined;

  let targetTimeSeconds: number;
  let message: string;

  if (metrics.runTimeImprovement !== undefined) {
    if (metrics.runTimeImprovement < -5) {
      // Improving - push for faster
      targetTimeSeconds = Math.round(metrics.avgRunTime * 0.97);  // 3% faster target
      message = ' You\'re getting faster! Push for a new PR';
    } else if (metrics.runTimeImprovement > 5) {
      // Declining - consolidate
      targetTimeSeconds = Math.round(metrics.avgRunTime);
      message = ' Focus on maintaining your pace this session';
    } else {
      // Maintaining - small push
      targetTimeSeconds = Math.round(metrics.avgRunTime * 0.98);  // 2% faster target
      message = ' Consistent performance - try to beat your average';
    }
  } else {
    // Not enough history
    targetTimeSeconds = Math.round(metrics.avgRunTime);
    message = ' Match your recent average pace';
  }

  return {
    targetTimeSeconds,
    displayTime: secondsToTime(targetTimeSeconds),
    message,
  };
}

/**
 * Get comprehensive circuit recommendation for a specific exercise
 */
export async function getCircuitRecommendation(
  exerciseName: string,
  currentTargetRounds: number = 5
): Promise<CircuitRecommendation> {
  const metrics = await analyzeCircuitPerformance(exerciseName);
  const adjustment = generateCircuitAdjustment(metrics, currentTargetRounds);

  // Calculate target pace based on history
  const history = await getCircuitHistory(exerciseName, 3);
  let targetPaceSeconds: number;

  if (history.length > 0) {
    // Target is best recent average minus small improvement
    const recentBest = Math.min(...history.map(h => h.avgTime));
    targetPaceSeconds = Math.round(recentBest * 0.98);  // 2% improvement target
  } else {
    // Default target
    targetPaceSeconds = 120;  // 2 minutes per round default
  }

  return {
    exerciseName,
    metrics,
    adjustment,
    targetPace: {
      roundTimeSeconds: targetPaceSeconds,
      displayTime: secondsToTime(targetPaceSeconds),
    },
    sprintRecommendation: generateSprintRecommendation(metrics),
  };
}

/**
 * Get recommendations for all circuits in a workout
 */
export async function getWorkoutCircuitRecommendations(
  circuits: { exerciseName: string; targetRounds: number }[]
): Promise<Map<string, CircuitRecommendation>> {
  const recommendations = new Map<string, CircuitRecommendation>();

  for (const circuit of circuits) {
    const rec = await getCircuitRecommendation(circuit.exerciseName, circuit.targetRounds);
    recommendations.set(circuit.exerciseName, rec);
  }

  return recommendations;
}

/**
 * Analyze cardio/sprint performance for adaptive recommendations
 * Used by the adaptive cardio system
 */
export async function analyzeSprintPerformance(
  exerciseNamePattern: string,
  sessionLimit: number = 10
): Promise<{
  avgTime: number;
  bestTime: number;
  trend: 'improving' | 'maintaining' | 'declining' | 'insufficient_data';
  recommendedTarget: number;
  message: string;
}> {
  const sessions = await getSessionHistory();
  const sprintData: { time: number; date: string }[] = [];

  for (const session of sessions.slice(0, sessionLimit * 3)) {
    // Skip range sessions (don't have exercises)
    if (session.sessionType === 'range' || !('exercises' in session) || !session.exercises) continue;

    for (const exercise of session.exercises) {
      const nameLower = exercise.exerciseName.toLowerCase();
      const patternLower = exerciseNamePattern.toLowerCase();

      if (nameLower.includes(patternLower) || patternLower.includes(nameLower)) {
        // Check circuitSets for run times
        if (exercise.circuitSets) {
          for (const set of exercise.circuitSets) {
            if (set.runTime) {
              const time = timeToSeconds(set.runTime);
              if (time > 0) {
                sprintData.push({
                  time,
                  date: typeof session.date === 'string' ? session.date : session.date.toISOString(),
                });
              }
            }
          }
        }

        // Also check regular time field for cardio exercises
        if (exercise.actualTime) {
          const time = timeToSeconds(exercise.actualTime);
          if (time > 0) {
            sprintData.push({
              time,
              date: typeof session.date === 'string' ? session.date : session.date.toISOString(),
            });
          }
        }
      }
    }

    if (sprintData.length >= sessionLimit) break;
  }

  if (sprintData.length === 0) {
    return {
      avgTime: 0,
      bestTime: 0,
      trend: 'insufficient_data',
      recommendedTarget: 60,  // Default 1 minute
      message: 'No sprint data available - track your times to get personalized targets',
    };
  }

  const times = sprintData.map(d => d.time);
  const avgTime = times.reduce((a, b) => a + b, 0) / times.length;
  const bestTime = Math.min(...times);

  // Determine trend
  let trend: 'improving' | 'maintaining' | 'declining' | 'insufficient_data' = 'insufficient_data';
  let recommendedTarget = avgTime;
  let message = '';

  if (sprintData.length >= 4) {
    const recent = times.slice(0, Math.floor(times.length / 2));
    const older = times.slice(Math.floor(times.length / 2));

    const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
    const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;

    const change = ((recentAvg - olderAvg) / olderAvg) * 100;

    if (change < -5) {
      trend = 'improving';
      recommendedTarget = Math.round(recentAvg * 0.97);
      message = ` Getting faster! Aim for ${secondsToTime(recommendedTarget)}`;
    } else if (change > 5) {
      trend = 'declining';
      recommendedTarget = Math.round(olderAvg);
      message = ` Focus on matching your previous best of ${secondsToTime(recommendedTarget)}`;
    } else {
      trend = 'maintaining';
      recommendedTarget = Math.round(recentAvg * 0.98);
      message = ` Consistent pace - push for ${secondsToTime(recommendedTarget)}`;
    }
  } else {
    message = ` Current average: ${secondsToTime(Math.round(avgTime))} - keep tracking for personalized targets`;
  }

  return {
    avgTime,
    bestTime,
    trend,
    recommendedTarget,
    message,
  };
}
